import logger from '../../utils/logger';
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "../../style/ImagingModals.css";

/**
 * Modal for requesting a change to an existing base or custom imaging entry.
 * Shows the current entry read-only. At least one of (proposed CPT code,
 * proposed name) must be filled in, and a reason is always required.
 *
 * Props:
 *  - isOpen     {boolean}          Whether the modal is visible
 *  - onClose    {function}         Called when the modal should close
 *  - entry      {object|null}      The entry being changed: { id, code, name, modality, region }
 *  - entryType  {'base'|'custom'}  Whether this is a hardcoded base entry or a custom one
 */
const ImagingChangeRequestModal = ({ isOpen, onClose, entry, entryType = "base", user = null }) => {
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [proposedCode, setProposedCode] = useState("");
  const [proposedName, setProposedName] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset fields whenever a new entry is loaded
  useEffect(() => {
    if (isOpen) {
      setProposedCode("");
      setProposedName("");
      setReason("");
    }
  }, [isOpen, entry]);

  if (!isOpen || !entry) return null;

  const handleClose = () => {
    setProposedCode("");
    setProposedName("");
    setReason("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!proposedCode.trim() && !proposedName.trim()) {
      toast.warning("Please propose a change to the CPT code or exam name (or both)");
      return;
    }
    if (!reason.trim()) {
      toast.warning("A reason for the change request is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/orders/imagingChangeRequest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          target_type: entryType,
          base_id: entryType === "base" ? entry.id : null,
          custom_id: entryType === "custom" ? entry.id : null,
          requested_cpt_code: proposedCode.trim() || null,
          requested_name: proposedName.trim() || null,
          reason: reason.trim(),
          user,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Change request submitted for review");
        handleClose();
      } else {
        toast.error(data.msg || "Failed to submit change request");
      }
    } catch (error) {
      logger.error("Error submitting change request:", error);
      toast.error("Error submitting change request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="im-modal-overlay" onClick={handleClose}>
      <div className="im-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="im-modal-header">
          <h3>Request Change</h3>
          <button className="im-modal-close" onClick={handleClose} disabled={isSubmitting}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="im-modal-body">
            {/* Current entry — read only */}
            <div className="im-current-entry">
              <p className="im-current-entry-label">Current Entry</p>
              <p><strong>CPT Code:</strong> {entry.code}</p>
              <p><strong>Name:</strong> {entry.name}</p>
              <p><strong>Modality:</strong> {entry.modality}</p>
              <p><strong>Region:</strong> {entry.region}</p>
            </div>

            {/* Proposed changes */}
            <div className="im-form-group">
              <label htmlFor="cr-code">
                Proposed CPT Code
                <span className="im-optional"> (optional)</span>
              </label>
              <input
                id="cr-code"
                type="text"
                className="im-input"
                value={proposedCode}
                onChange={(e) => setProposedCode(e.target.value)}
                placeholder={entry.code}
                disabled={isSubmitting}
              />
            </div>

            <div className="im-form-group">
              <label htmlFor="cr-name">
                Proposed Exam Name
                <span className="im-optional"> (optional)</span>
              </label>
              <input
                id="cr-name"
                type="text"
                className="im-input"
                value={proposedName}
                onChange={(e) => setProposedName(e.target.value)}
                placeholder={entry.name}
                disabled={isSubmitting}
              />
            </div>

            <div className="im-form-group">
              <label htmlFor="cr-reason">
                Reason for Change
                <span className="im-required"> *</span>
              </label>
              <textarea
                id="cr-reason"
                className="im-textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Explain why this change is needed..."
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="im-modal-footer">
            <button
              type="button"
              className="im-btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="im-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImagingChangeRequestModal;
